import React, { useState, useEffect, useRef } from 'react';
import {
  Building2, Plus, Search, Loader2, ChevronRight,
  Pencil, FileText, TrendingUp, BarChart2,
  ShieldCheck, ShieldAlert, Upload, Trash2, KeyRound,
  RefreshCw, CloudDownload, CheckCircle2, RotateCcw
} from 'lucide-react';
import api from '../services/api';
import toast from 'react-hot-toast';
import { formatarCnpj, formatarNumero } from '../utils/formatters';
import { useAuth } from '../contexts/AuthContext';
import Modal from '../components/common/Modal';

const FORM_VAZIO = {
  cnpj: '', razao_social: '', nome_fantasia: '',
  ie: '', email: '', telefone: '', municipio: '', uf: ''
};

const UFS = ['AC','AL','AM','AP','BA','CE','DF','ES','GO','MA','MG','MS','MT','PA','PB','PE','PI','PR','RJ','RN','RO','RR','RS','SC','SE','SP','TO'];

function formatarCnpjInput(v) {
  const n = v.replace(/\D/g, '').substring(0, 14);
  if (n.length <= 2) return n;
  if (n.length <= 5) return n.replace(/^(\d{2})(\d+)/, '$1.$2');
  if (n.length <= 8) return n.replace(/^(\d{2})(\d{3})(\d+)/, '$1.$2.$3');
  if (n.length <= 12) return n.replace(/^(\d{2})(\d{3})(\d{3})(\d+)/, '$1.$2.$3/$4');
  return n.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d+)/, '$1.$2.$3/$4-$5');
}

export default function EmpresasPage() {
  const { usuario }       = useAuth();
  const [empresas,     setEmpresas]     = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [busca,        setBusca]        = useState('');
  const [modalAberto,  setModalAberto]  = useState(false);
  const [editando,     setEditando]     = useState(null);
  const [form,         setForm]         = useState(FORM_VAZIO);
  const [salvando,     setSalvando]     = useState(false);
  const [detalhe,      setDetalhe]      = useState(null);
  const [resumo,       setResumo]       = useState(null);

  // Certificado
  const [certStatus,   setCertStatus]   = useState(null);
  const [certModal,    setCertModal]    = useState(false);
  const [certForm,     setCertForm]     = useState({ senha: '', validade: '' });
  const [certArquivo,  setCertArquivo]  = useState(null);
  const [certSalvando, setCertSalvando] = useState(false);
  const certInputRef = useRef();

  // NFeDistDFe
  const [nfedistStatus,      setNfedistStatus]      = useState(null);
  const [nfedistCarregando,  setNfedistCarregando]  = useState(false);
  const [reprocessando,      setReprocessando]      = useState(false);

  useEffect(() => { carregar(); }, []);

  async function carregar() {
    setLoading(true);
    try {
      const { data } = await api.get('/empresas');
      setEmpresas(data);
    } catch {
      toast.error('Erro ao carregar empresas.');
    } finally {
      setLoading(false);
    }
  }

  async function abrirDetalhe(emp) {
    setDetalhe(emp);
    setResumo(null);
    setCertStatus(null);
    setNfedistStatus(null);
    try {
      const [{ data: res }, { data: cert }, { data: nfe }] = await Promise.allSettled([
        api.get(`/empresas/${emp.id}/resumo`),
        api.get(`/certificados/${emp.id}`),
        api.get(`/nfedist/${emp.id}/status`)
      ]).then(rs => rs.map(r => r.status === 'fulfilled' ? r.value : { data: null }));
      setResumo(res);
      setCertStatus(cert);
      setNfedistStatus(nfe);
    } catch { /* nao critico */ }
  }

  async function consultarNFeDist() {
    if (!detalhe) return;
    setNfedistCarregando(true);
    const t = toast.loading('Consultando SEFAZ...');
    try {
      const { data } = await api.post(`/nfedist/${detalhe.id}`);
      setNfedistStatus(prev => ({
        ...prev,
        ultima_consulta: new Date().toISOString(),
        ultimo_nsu:      data.ultimo_nsu,
        total_recebidos: (prev?.total_recebidos || 0) + data.recebidos
      }));
      toast.success(data.mensagem, { id: t });
      // Atualiza o resumo de documentos também
      const { data: res } = await api.get(`/empresas/${detalhe.id}/resumo`);
      setResumo(res);
    } catch (err) {
      toast.error(err.response?.data?.erro || 'Erro ao consultar SEFAZ.', { id: t });
    } finally {
      setNfedistCarregando(false);
    }
  }

  async function reprocessarNFeDist() {
    if (!detalhe) return;
    const ok = window.confirm(
      `Isso vai zerar o NSU e baixar TODAS as NF-es da SEFAZ novamente para:\n\n${detalhe.razao_social}\n\nDocumentos já importados não serão duplicados. Continuar?`
    );
    if (!ok) return;
    setReprocessando(true);
    const t = toast.loading('Reprocessando desde o início... (pode demorar)');
    try {
      const { data } = await api.post(`/nfedist/${detalhe.id}/reprocessar`);
      setNfedistStatus({
        ultima_consulta: new Date().toISOString(),
        ultimo_nsu:      data.ultimo_nsu,
        total_recebidos: data.recebidos + (data.resumos || 0),
        ultimo_erro:     null
      });
      toast.success(data.mensagem, { id: t });
      const { data: res } = await api.get(`/empresas/${detalhe.id}/resumo`);
      setResumo(res);
    } catch (err) {
      toast.error(err.response?.data?.erro || 'Erro no reprocessamento.', { id: t });
    } finally {
      setReprocessando(false);
    }
  }

  async function salvarCertificado() {
    if (!certArquivo) return toast.error('Selecione o arquivo .pfx.');
    if (!certForm.senha) return toast.error('Informe a senha do certificado.');
    setCertSalvando(true);
    try {
      const fd = new FormData();
      fd.append('pfx', certArquivo);
      fd.append('senha', certForm.senha);
      if (certForm.validade) fd.append('validade', certForm.validade);
      const { data } = await api.post(`/certificados/${detalhe.id}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setCertStatus(data.certificado);
      setCertModal(false);
      setCertForm({ senha: '', validade: '' });
      setCertArquivo(null);
      toast.success('Certificado salvo com sucesso!');
    } catch (err) {
      toast.error(err.response?.data?.erro || 'Erro ao salvar certificado.');
    } finally {
      setCertSalvando(false);
    }
  }

  async function removerCertificado() {
    if (!window.confirm('Remover o certificado desta empresa?')) return;
    try {
      await api.delete(`/certificados/${detalhe.id}`);
      setCertStatus(null);
      toast.success('Certificado removido.');
    } catch (err) {
      toast.error(err.response?.data?.erro || 'Erro ao remover certificado.');
    }
  }

  function abrirNova() {
    setEditando(null);
    setForm(FORM_VAZIO);
    setModalAberto(true);
  }

  function abrirEditar(emp, e) {
    e.stopPropagation();
    setEditando(emp);
    setForm({
      cnpj: emp.cnpj, razao_social: emp.razao_social,
      nome_fantasia: emp.nome_fantasia || '',
      ie: emp.ie || '', email: emp.email || '',
      telefone: emp.telefone || '', municipio: emp.municipio || '', uf: emp.uf || ''
    });
    setModalAberto(true);
  }

  async function salvar() {
    if (!form.cnpj || !form.razao_social) return toast.error('CNPJ e Razao Social sao obrigatorios.');
    setSalvando(true);
    try {
      if (editando) {
        const { data } = await api.put(`/empresas/${editando.id}`, { ...form, ativa: editando.ativa });
        setEmpresas(es => es.map(e => e.id === data.id ? data : e));
        toast.success('Empresa atualizada.');
      } else {
        const { data } = await api.post('/empresas', form);
        setEmpresas(es => [...es, data]);
        toast.success('Empresa cadastrada com sucesso.');
      }
      setModalAberto(false);
    } catch (err) {
      toast.error(err.response?.data?.erro || 'Erro ao salvar empresa.');
    } finally {
      setSalvando(false);
    }
  }

  const filtradas = empresas.filter(e =>
    e.razao_social.toLowerCase().includes(busca.toLowerCase()) ||
    e.cnpj.includes(busca.replace(/\D/g, ''))
  );

  if (loading) return <div className="flex justify-center py-20"><Loader2 size={28} className="animate-spin text-primary-500" /></div>;

  if (detalhe) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <button onClick={() => setDetalhe(null)} className="btn-secondary text-sm py-1.5 px-3">
            Voltar
          </button>
          <h2 className="text-sm font-semibold text-slate-300">{detalhe.razao_social}</h2>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { label: 'NF-e',     val: formatarNumero(resumo?.total_nfe || 0),       icon: FileText,   cor: 'text-primary-400' },
            { label: 'CT-e',     val: formatarNumero(resumo?.total_cte || 0),        icon: TrendingUp, cor: 'text-emerald-400' },
            { label: 'MDF-e',    val: formatarNumero(resumo?.total_mdfe || 0),       icon: BarChart2,  cor: 'text-amber-400' },
            { label: 'Este mes', val: formatarNumero(resumo?.documentos_mes || 0),   icon: FileText,   cor: 'text-violet-400' },
          ].map(({ label, val, icon: Icon, cor }) => (
            <div key={label} className="card flex items-center gap-3">
              <Icon size={18} className={cor} />
              <div>
                <p className="text-xs text-slate-500">{label}</p>
                <p className="text-xl font-bold text-white">{resumo ? val : '—'}</p>
              </div>
            </div>
          ))}
        </div>
        {/* Certificado Digital */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <KeyRound size={16} className="text-primary-400" />
              <h3 className="text-sm font-semibold text-slate-300">Certificado Digital A1</h3>
            </div>
            {usuario?.perfil !== 'cliente' && (
              <div className="flex gap-2">
                <button onClick={() => setCertModal(true)} className="btn-primary text-xs py-1.5 px-3">
                  <Upload size={13} /> {certStatus ? 'Substituir' : 'Enviar Certificado'}
                </button>
                {certStatus && usuario?.perfil === 'admin' && (
                  <button onClick={removerCertificado} className="btn-secondary text-xs py-1.5 px-3 text-red-400 hover:text-red-300">
                    <Trash2 size={13} />
                  </button>
                )}
              </div>
            )}
          </div>

          {certStatus ? (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
              <div className="flex items-center gap-2 col-span-2 lg:col-span-1">
                {certStatus.validade && new Date(certStatus.validade) < new Date()
                  ? <ShieldAlert size={18} className="text-red-400 flex-shrink-0" />
                  : <ShieldCheck size={18} className="text-emerald-400 flex-shrink-0" />
                }
                <div>
                  <p className="text-xs text-slate-600">Situacao</p>
                  <p className={certStatus.validade && new Date(certStatus.validade) < new Date() ? 'text-red-400' : 'text-emerald-400'}>
                    {certStatus.validade && new Date(certStatus.validade) < new Date() ? 'Vencido' : 'Valido'}
                  </p>
                </div>
              </div>
              <div>
                <p className="text-xs text-slate-600">Arquivo</p>
                <p className="text-slate-300 truncate max-w-[160px]">{certStatus.nome_arquivo || 'certificado.pfx'}</p>
              </div>
              <div>
                <p className="text-xs text-slate-600">Validade</p>
                <p className="text-slate-300">{certStatus.validade ? new Date(certStatus.validade).toLocaleDateString('pt-BR') : 'Nao informada'}</p>
              </div>
              <div>
                <p className="text-xs text-slate-600">Thumbprint</p>
                <p className="text-slate-500 font-mono text-xs truncate">{certStatus.thumbprint?.slice(0, 20)}...</p>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-2 text-slate-600 text-sm">
              <ShieldAlert size={16} className="text-amber-500" />
              Nenhum certificado cadastrado. Envie o arquivo .pfx para habilitar manifestacao e consulta SEFAZ.
            </div>
          )}
        </div>

        {/* NFeDistDFe — só aparece se tiver certificado */}
        {certStatus && (
          <div className="card">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <CloudDownload size={16} className="text-sky-400" />
                <h3 className="text-sm font-semibold text-slate-300">Buscar NF-es na SEFAZ (NFeDistDFe)</h3>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={reprocessarNFeDist}
                  disabled={nfedistCarregando || reprocessando}
                  title="Zera o NSU e baixa tudo da SEFAZ do início — útil para recuperação após perda de dados"
                  className="btn-secondary text-xs py-1.5 px-3 text-amber-400 hover:text-amber-300 disabled:opacity-50">
                  {reprocessando
                    ? <><Loader2 size={13} className="animate-spin" /> Reprocessando...</>
                    : <><RotateCcw size={13} /> Reprocessar do início</>}
                </button>
                <button
                  onClick={consultarNFeDist}
                  disabled={nfedistCarregando || reprocessando}
                  className="btn-primary text-xs py-1.5 px-3 disabled:opacity-50">
                  {nfedistCarregando
                    ? <><Loader2 size={13} className="animate-spin" /> Consultando...</>
                    : <><RefreshCw size={13} /> Consultar Agora</>}
                </button>
              </div>
            </div>

            {nfedistStatus?.ultima_consulta ? (
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div>
                  <p className="text-xs text-slate-600">Última consulta</p>
                  <p className="text-slate-300">
                    {new Date(nfedistStatus.ultima_consulta).toLocaleString('pt-BR')}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-slate-600">NSU atual</p>
                  <p className="text-slate-300 font-mono">{String(nfedistStatus.ultimo_nsu).padStart(15, '0')}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-600">Total recebido</p>
                  <div className="flex items-center gap-1">
                    <CheckCircle2 size={14} className="text-emerald-400" />
                    <p className="text-emerald-400 font-semibold">{nfedistStatus.total_recebidos} NF-e(s)</p>
                  </div>
                </div>
                {nfedistStatus.ultimo_erro && (
                  <div className="col-span-3 text-xs text-red-400 bg-red-900/20 border border-red-800/30 rounded-lg p-2">
                    Último erro: {nfedistStatus.ultimo_erro}
                  </div>
                )}
              </div>
            ) : (
              <p className="text-sm text-slate-600">
                Nenhuma consulta realizada ainda. Clique em "Consultar Agora" ou aguarde o cron automático (6h e 18h).
              </p>
            )}
          </div>
        )}

        <div className="card">
          <h3 className="text-sm font-semibold text-slate-300 mb-4">Dados Cadastrais</h3>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
            {[
              ['CNPJ',           formatarCnpj(detalhe.cnpj)],
              ['Razao Social',   detalhe.razao_social],
              ['Nome Fantasia',  detalhe.nome_fantasia || '—'],
              ['Inscr. Estadual',detalhe.ie || '—'],
              ['E-mail',         detalhe.email || '—'],
              ['Telefone',       detalhe.telefone || '—'],
              ['Municipio',      detalhe.municipio ? detalhe.municipio + '/' + detalhe.uf : '—'],
              ['Situacao',       detalhe.ativa ? 'Ativa' : 'Inativa'],
            ].map(([k, v]) => (
              <div key={k}>
                <p className="text-xs text-slate-600 mb-0.5">{k}</p>
                <p className="text-slate-300">{v}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <input className="input text-sm pl-8" placeholder="Buscar por nome ou CNPJ..."
            value={busca} onChange={e => setBusca(e.target.value)} />
        </div>
        {usuario?.perfil === 'admin' && (
          <button onClick={abrirNova} className="btn-primary text-sm py-2">
            <Plus size={15} /> Nova Empresa
          </button>
        )}
      </div>

      <p className="text-xs text-slate-600">{filtradas.length} empresa(s)</p>

      <div className="grid gap-2">
        {filtradas.length === 0 ? (
          <div className="card text-center py-12 text-slate-600">
            <Building2 size={32} className="mx-auto mb-2 opacity-30" />
            Nenhuma empresa encontrada
          </div>
        ) : filtradas.map(emp => (
          <div key={emp.id} onClick={() => abrirDetalhe(emp)}
            className="card hover:border-primary-600/40 transition-all cursor-pointer group py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-primary-900/40 border border-primary-800/30 flex items-center justify-center flex-shrink-0">
                  <Building2 size={16} className="text-primary-400" />
                </div>
                <div>
                  <p className="font-semibold text-slate-200 text-sm">{emp.razao_social}</p>
                  <p className="text-xs text-slate-500 mt-0.5">
                    {formatarCnpj(emp.cnpj)}
                    {emp.municipio && ' · ' + emp.municipio + '/' + emp.uf}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className={'text-xs px-2 py-0.5 rounded-full ' + (emp.ativa ? 'bg-emerald-900/40 text-emerald-400' : 'bg-red-900/40 text-red-400')}>
                  {emp.ativa ? 'Ativa' : 'Inativa'}
                </span>
                {usuario?.perfil === 'admin' && (
                  <button onClick={(e) => abrirEditar(emp, e)}
                    className="p-1.5 rounded-lg text-slate-600 hover:text-primary-400 hover:bg-primary-900/20 transition-colors opacity-0 group-hover:opacity-100">
                    <Pencil size={14} />
                  </button>
                )}
                <ChevronRight size={15} className="text-slate-600 group-hover:text-primary-400 transition-colors" />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Modal Certificado */}
      <Modal aberto={certModal} onFechar={() => setCertModal(false)} titulo="Enviar Certificado Digital A1">
        <div className="space-y-4">
          <div>
            <label className="label">Arquivo .pfx *</label>
            <div
              onClick={() => certInputRef.current?.click()}
              className="border-2 border-dashed border-surface-border rounded-xl p-6 text-center cursor-pointer hover:border-primary-600/50 transition-colors">
              <Upload size={24} className="mx-auto mb-2 text-slate-600" />
              <p className="text-sm text-slate-500">
                {certArquivo ? certArquivo.name : 'Clique para selecionar o arquivo .pfx'}
              </p>
              <input ref={certInputRef} type="file" accept=".pfx" className="hidden"
                onChange={e => setCertArquivo(e.target.files[0])} />
            </div>
          </div>
          <div>
            <label className="label">Senha do Certificado *</label>
            <input className="input font-mono" type="password" placeholder="Senha do arquivo .pfx"
              value={certForm.senha} onChange={e => setCertForm(f => ({...f, senha: e.target.value}))} />
          </div>
          <div>
            <label className="label">Data de Validade</label>
            <input className="input" type="date" value={certForm.validade}
              onChange={e => setCertForm(f => ({...f, validade: e.target.value}))} />
            <p className="text-xs text-slate-600 mt-1">Opcional — para controle de vencimento</p>
          </div>
          <div className="bg-amber-900/20 border border-amber-700/30 rounded-lg p-3 text-xs text-amber-400">
            O certificado e a senha sao criptografados antes de armazenar. A senha nunca fica em texto puro.
          </div>
        </div>
        <div className="flex gap-2 justify-end pt-4 mt-2 border-t border-surface-border">
          <button onClick={() => setCertModal(false)} className="btn-secondary text-sm py-2 px-4">Cancelar</button>
          <button onClick={salvarCertificado} disabled={certSalvando} className="btn-primary text-sm py-2 px-4">
            {certSalvando ? <><Loader2 size={14} className="animate-spin" /> Enviando...</> : 'Salvar Certificado'}
          </button>
        </div>
      </Modal>

      <Modal aberto={modalAberto} onFechar={() => setModalAberto(false)}
        titulo={editando ? 'Editar Empresa' : 'Nova Empresa'} tamanho="lg">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div>
            <label className="label">CNPJ *</label>
            <input className="input font-mono" value={form.cnpj}
              onChange={e => setForm(f => ({...f, cnpj: formatarCnpjInput(e.target.value)}))}
              placeholder="00.000.000/0001-00" disabled={!!editando} />
          </div>
          <div>
            <label className="label">Inscricao Estadual</label>
            <input className="input" value={form.ie} onChange={e => setForm(f => ({...f, ie: e.target.value}))} placeholder="Opcional" />
          </div>
          <div className="lg:col-span-2">
            <label className="label">Razao Social *</label>
            <input className="input" value={form.razao_social}
              onChange={e => setForm(f => ({...f, razao_social: e.target.value.toUpperCase()}))}
              placeholder="EMPRESA EXEMPLO LTDA" />
          </div>
          <div className="lg:col-span-2">
            <label className="label">Nome Fantasia</label>
            <input className="input" value={form.nome_fantasia}
              onChange={e => setForm(f => ({...f, nome_fantasia: e.target.value}))} placeholder="Opcional" />
          </div>
          <div>
            <label className="label">E-mail</label>
            <input className="input" type="email" value={form.email}
              onChange={e => setForm(f => ({...f, email: e.target.value}))} placeholder="empresa@email.com.br" />
          </div>
          <div>
            <label className="label">Telefone</label>
            <input className="input" value={form.telefone}
              onChange={e => setForm(f => ({...f, telefone: e.target.value}))} placeholder="(17) 99999-9999" />
          </div>
          <div>
            <label className="label">Municipio</label>
            <input className="input" value={form.municipio}
              onChange={e => setForm(f => ({...f, municipio: e.target.value}))} placeholder="Sao Paulo" />
          </div>
          <div>
            <label className="label">UF</label>
            <select className="input" value={form.uf} onChange={e => setForm(f => ({...f, uf: e.target.value}))}>
              <option value="">Selecione...</option>
              {UFS.map(uf => <option key={uf} value={uf}>{uf}</option>)}
            </select>
          </div>
        </div>
        <div className="flex gap-2 justify-end pt-4 mt-2 border-t border-surface-border">
          <button onClick={() => setModalAberto(false)} className="btn-secondary text-sm py-2 px-4">Cancelar</button>
          <button onClick={salvar} disabled={salvando} className="btn-primary text-sm py-2 px-4">
            {salvando ? <><Loader2 size={14} className="animate-spin" /> Salvando...</> : 'Salvar'}
          </button>
        </div>
      </Modal>
    </div>
  );
}
